import { computed, ref } from 'vue';
import { useAlert } from 'dashboard/composables';
import ContactAPI from 'dashboard/api/contacts';
import ContactConsentsAPI from 'dashboard/api/contactConsents';
import AnalyticsEventsAPI from 'dashboard/api/analyticsEvents';
import { consentRecordFor } from '../utils/minhaLaisPresenters';

/**
 * Notification preferences (consents) of a selected contact, with the
 * notimanager analytics events emitted by the original panel.
 */
export function useMinhaLaisNotifications() {
  const notificationSearch = ref('');
  const notificationContacts = ref([]);
  const notificationContactId = ref(null);
  const notificationProfile = ref('TENANT');
  const consents = ref([]);
  const isSavingConsents = ref(false);
  const currentAccountId =
    typeof window !== 'undefined'
      ? window.location.pathname.split('/')[3]
      : null;

  const notificationContactOptions = computed(() =>
    notificationContacts.value.map(contact => ({
      value: contact.id,
      label: [contact.name, contact.email || contact.phone_number]
        .filter(Boolean)
        .join(' · '),
    }))
  );

  const selectedNotificationContact = computed(
    () =>
      notificationContacts.value.find(
        contact => Number(contact.id) === Number(notificationContactId.value)
      ) || null
  );

  const trackNotificationEvent = async (name, properties = {}) => {
    if (!currentAccountId) return;

    try {
      await AnalyticsEventsAPI.create({
        account_id: currentAccountId,
        contact_id: notificationContactId.value || undefined,
        name,
        source: 'web',
        properties,
      });
    } catch {
      // no-op
    }
  };

  const carregarNotificationContacts = async () => {
    try {
      const query = notificationSearch.value.trim();
      const response = query
        ? await ContactAPI.search(query, 1, 'name')
        : await ContactAPI.get(1, 'name');
      notificationContacts.value = response.data.payload || [];
    } catch {
      notificationContacts.value = [];
    }
  };

  const ensureNotificationTabReady = async () => {
    if (notificationContacts.value.length === 0) {
      await carregarNotificationContacts();
    }
    await trackNotificationEvent(
      'notimanager_manage_notifications_page_viewed'
    );
  };

  const carregarConsents = async () => {
    if (!notificationContactId.value) {
      consents.value = [];
      return;
    }

    try {
      const { data } = await ContactConsentsAPI.list(
        notificationContactId.value
      );
      consents.value = data.data || [];
    } catch {
      consents.value = [];
      useAlert('Não foi possível carregar os consentimentos do cliente.');
    }
  };

  const onNotificationContactChange = async value => {
    notificationContactId.value = Number(value) || null;
    await carregarConsents();
  };

  const selectNotificationProfile = async profile => {
    notificationProfile.value = profile;
    await trackNotificationEvent('notimanager_profile_clicked', { profile });
  };

  const salvarConsent = async (purpose, channel, enabled) => {
    if (!notificationContactId.value) {
      useAlert('Selecione um cliente para editar as notificações.');
      return;
    }

    isSavingConsents.value = true;
    try {
      const record = consentRecordFor(
        consents.value,
        purpose,
        channel,
        notificationProfile.value
      );
      const payload = {
        purpose,
        channel,
        profile: notificationProfile.value,
        status: enabled ? 'ACTIVE' : 'WITHDRAWN',
      };

      if (record) {
        await ContactConsentsAPI.update(
          notificationContactId.value,
          record.id,
          payload
        );
      } else {
        await ContactConsentsAPI.create(notificationContactId.value, payload);
      }

      await carregarConsents();
      await trackNotificationEvent('notimanager_anyconfig_clicked', {
        purpose,
        channel,
        profile: notificationProfile.value,
        status: payload.status,
      });
    } catch (error) {
      useAlert(
        error.response?.data?.errors?.[0] ||
          'Não foi possível salvar o consentimento.'
      );
    } finally {
      isSavingConsents.value = false;
    }
  };

  const aplicarTodosOsPerfis = async (purpose, channel, enabled) => {
    if (!notificationContactId.value) {
      useAlert('Selecione um cliente para aplicar em todos os perfis.');
      return;
    }

    isSavingConsents.value = true;
    try {
      await ContactConsentsAPI.applyAll(notificationContactId.value, {
        purpose,
        channel,
        status: enabled ? 'ACTIVE' : 'WITHDRAWN',
      });
      await carregarConsents();
      await trackNotificationEvent('notimanager_allprofiles_clicked', {
        purpose,
        channel,
        status: enabled ? 'ACTIVE' : 'WITHDRAWN',
      });
    } catch (error) {
      useAlert(
        error.response?.data?.errors?.[0] ||
          'Não foi possível aplicar o consentimento para todos os perfis.'
      );
    } finally {
      isSavingConsents.value = false;
    }
  };

  const trackTooltipClick = () =>
    trackNotificationEvent('notimanager_tooltip_clicked', {
      profile: notificationProfile.value,
    });

  return {
    notificationSearch,
    notificationContactId,
    notificationProfile,
    consents,
    isSavingConsents,
    notificationContactOptions,
    selectedNotificationContact,
    carregarNotificationContacts,
    ensureNotificationTabReady,
    onNotificationContactChange,
    selectNotificationProfile,
    salvarConsent,
    aplicarTodosOsPerfis,
    trackTooltipClick,
  };
}
